/**
 * Section 20c — Risk Disclaimer (PRE_LAUNCH CL3 / F4).
 *
 * Plain-English, India-aware risk disclosure for self-directed retail
 * traders using the Platform to route orders to Indian (NSE/BSE/MCX via
 * Zerodha) and offshore-crypto (Delta Exchange) venues. Authored from
 * general knowledge of SEBI norms; not a substitute for counsel review.
 */
import React from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GuideCallout from '../GuideCallout';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 3.5, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const L = ({ children }: { children: React.ReactNode }) => (
  <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>{children}</Box>
);
const Li = ({ children }: { children: React.ReactNode }) => (
  <Box component="li" sx={{ mb: 0.5, fontSize: '0.95rem', lineHeight: 1.6 }}>{children}</Box>
);

const Section_risk: GuideSection = {
  id: 'risk-disclaimer',
  label: 'Risk Disclaimer',
  group: 'help',
  icon: <WarningAmberIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>Risk Disclaimer</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Effective: 2026-05-30 · Version 2026.05 · Read before placing your first real-money order
      </Typography>

      <GuideCallout kind="danger" title="You can lose money — possibly all of it">
        <P>
          Trading in equities, equity derivatives (futures &amp; options), commodity
          derivatives, and cryptocurrency derivatives involves <strong>substantial
          risk of loss</strong> and is not suitable for every investor. You can lose
          part or all of the capital you trade. With leveraged products (intraday
          margin trades, futures, options shorts, crypto perpetuals on Delta
          Exchange) <strong>losses can exceed the margin you deposit</strong> and
          may require you to bring in additional funds.
        </P>
      </GuideCallout>

      <H id="self-directed">1. You are a self-directed trader</H>
      <P>
        The Platform is a self-directed technical-analysis and order-routing tool.
        Every decision — to enter, exit, scale, hedge, square-off, or stay out — is
        yours alone. The Platform's scanners (Sniper, RockBand, DailyBlast), chart
        overlays, alerts, and back-tests are <strong>analytical aids, not buy/sell
        recommendations</strong>. The Operator is not a SEBI-registered Investment
        Adviser, Research Analyst, or Portfolio Manager.
      </P>

      <H id="not-advice">2. Past performance is not future performance</H>
      <P>
        Back-tested and simulated performance has well-known limitations: it is
        constructed with the benefit of hindsight, may not account for slippage,
        partial fills, latency, or fees, and does not reflect the psychological
        difficulty of executing a strategy in real time. Real-world results will
        differ — sometimes substantially — from historical results. Any equity
        curve, win-rate, or Sharpe ratio shown on the Platform is hypothetical
        unless it is presented as a record of actual live trades.
      </P>

      <H id="leverage">3. Leverage amplifies both wins and losses</H>
      <L>
        <Li>
          <strong>Equity intraday (MIS):</strong> brokers offer leverage on intraday
          equity. A 5× leveraged position moves your account equity 5× as fast in
          both directions. A 20% adverse move on a 5× leveraged trade wipes 100%
          of the trade's notional margin.
        </Li>
        <Li>
          <strong>F&amp;O (futures and options):</strong> futures involve the full
          notional of the contract on a fraction of margin. Short option positions
          have theoretically unbounded loss potential. SEBI's peak-margin rules,
          T+1 settlement, and physical-settlement risk for in-the-money options
          held to expiry all apply.
        </Li>
        <Li>
          <strong>Crypto derivatives on Delta Exchange:</strong> perpetual swaps
          and futures with up to 100× leverage. A 1% adverse move on a 100×
          position liquidates the full margin. Funding rates, exchange downtime,
          and the offshore (not RBI-regulated) nature of the venue add further
          risk.
        </Li>
      </L>

      <H id="automation">4. Automation can fail — silently or loudly</H>
      <P>
        The Platform offers automated order placement via TradingView webhooks,
        internal scanners, and time-triggered jobs. By enabling Auto-Trade you
        authorise live orders to be placed without further per-order confirmation.
        Automation can:
      </P>
      <L>
        <Li>Fire <strong>unintended orders</strong> due to webhook spoofing (mitigated by per-user X-Webhook-Secret), bugs, or data-feed corruption.</Li>
        <Li>Fail to fire <strong>intended orders</strong> due to broker outages, network failures, rate limits, or the Platform itself being down.</Li>
        <Li>Place orders at <strong>unexpected prices</strong> due to slippage in fast markets, particularly at market open, close, news events, and around expiry.</Li>
        <Li>Get <strong>stuck</strong> between partial fills, leaving you with a position different from what you intended.</Li>
      </L>
      <P>
        You agree to monitor positions independently. You will not rely on
        automation as your only safeguard. You will disable Auto-Trade and
        intervene manually when needed. The Platform's daily-loss cap and kill
        switch are last-resort circuit breakers, not substitutes for active
        oversight.
      </P>

      <H id="data-feed">5. Data feeds and execution are not the Platform's responsibility</H>
      <P>
        Quote feeds, tick streams, candle data, and execution quality originate
        with brokers and exchanges. The Platform displays this data on a
        best-effort basis. Stale ticks, gapped candles, post-only rejects, freeze
        upon limit-order modification, and other broker-side conditions can and
        do happen. <strong>Always verify positions and orders directly with your
        broker before assuming the on-screen state is correct</strong>, especially
        after any disconnection or restart.
      </P>

      <H id="regulatory">6. Regulatory context</H>
      <L>
        <Li>
          Indian equity, F&amp;O, and commodity trading is regulated by SEBI, NSE,
          BSE, and MCX. Your broker is the SEBI-registered intermediary; the
          Platform is not. SEBI's circulars on algorithmic trading and retail
          access are evolving — you are responsible for compliance with rules
          applicable to your specific use of automated trading.
        </Li>
        <Li>
          Cryptocurrency derivatives (Delta Exchange) operate offshore and are{' '}
          <em>not</em> regulated by SEBI or RBI. Domestic Indian-rupee on-/off-ramps
          for these venues are subject to changing RBI and FIU-IND norms. You are
          responsible for ensuring your participation complies with applicable
          law including the Income-tax Act (Section 115BBH for virtual digital
          assets at 30% + cess + 1% TDS u/s 194S) and Prevention of Money
          Laundering Act obligations.
        </Li>
        <Li>
          Tax treatment: equity intraday profits are speculative business income;
          F&amp;O is non-speculative business income; LTCG/STCG rules apply to
          delivery trades. Cross-margin, hedge-set-offs, and carry-forward rules
          are complex. Consult a chartered accountant.
        </Li>
      </L>

      <H id="black-swan">7. Black-swan and exchange-wide events</H>
      <P>
        Market events outside ordinary risk modelling do happen: lower-circuits
        and upper-circuits on individual scrips, exchange-wide circuit breakers
        on the index, broker outages on high-volatility days, regulator-imposed
        bans on entire categories of trades (F&amp;O ban-period, short-selling
        restrictions), and venue-specific halts on crypto exchanges. During such
        events, positions cannot be closed and stop-loss orders may not trigger.
        The Platform cannot insulate you from these events.
      </P>

      <H id="suitability">8. Suitability</H>
      <P>
        Before using the Platform with real money, satisfy yourself that you:
      </P>
      <L>
        <Li>Have <strong>risk capital</strong> — money you can fully lose without affecting your financial wellbeing, dependants, or essential obligations.</Li>
        <Li>Understand the mechanics of every product you intend to trade (lot size, expiry conventions, settlement type, margin formula, funding rates).</Li>
        <Li>Have a written plan for what you will do if you reach your daily-loss cap, if Auto-Trade fires unexpectedly, and if your broker connection drops while you have an open position.</Li>
        <Li>Have a manual order-cancellation path (broker terminal, broker mobile app, broker call-and-trade) and have practised using it.</Li>
      </L>

      <H id="ack">9. Your acknowledgement</H>
      <P>
        By clicking <strong>"I have read and agree"</strong> on the legal-acceptance
        gate, you acknowledge that you have read and understood this Risk
        Disclaimer, that you trade at your own risk, and that the Operator is
        not liable for your trading losses as further specified in the Terms of
        Service and the Privacy Policy.
      </P>

      <GuideCallout kind="warning" title="Last reminder">
        Never trade money you can't afford to lose. Never disable the daily-loss
        circuit breaker. Never increase position size to "make back" a losing
        day. If you find yourself wanting to do any of these things, log out
        and step away.
      </GuideCallout>
    </>
  ),
};

export default Section_risk;
